import { useImperativeHandle, useRef, useState } from 'react';
import type { FormEvent, Ref } from 'react';
import { useTranslation } from 'react-i18next';

import { DataGrid, Divider, NgButton, NgModal, ReadOnlyValue } from '@/ui';
import type { NgModalHandle } from '@/ui';
import { useEditForm } from '@/ui/forms/useEditForm';
import { usePsapConfiguration } from '@/app/shell/psapConfiguration';
import { useTopology } from '@/app/topology';
import { PhoneNumber } from '@/lib/phone/phoneNumber';
import type { Position } from '@/signalr';
import { sendSmsMessage } from '../actions/emsCommands';
import {
  CreateShareLinkTargetError,
  createIncidentShareLink,
  createShareLinkTarget,
  findShareLinkTargetByPhone,
  getShareLinkTargets,
} from '../api/identityShareApi';
import type { ShareLinkTargetDto } from '../api/identityShareApi';
import { formatCoordinates } from '../model/formatters';
import {
  extractPhoneCandidate,
  filterShareLinkTargets,
  insertTargetSorted,
  normalizePhone,
  tryExtractApiErrorMessage,
  validateNewUserDisplayName,
  validateNewUserPhoneNumber,
} from './shareByLinkModel';

const NEW_USER_FORM_ID = 'share-by-link-new-user';

type Step = 'pickContact' | 'createNewContact' | 'shareLink';

export type ShareByLinkDialogHandle = {
  showModal: () => void;
};

export type ShareByLinkDialogProps = {
  incidentId: string;
  lastCallerPosition: Position | null;
  ref?: Ref<ShareByLinkDialogHandle>;
};

export function ShareByLinkDialog({ incidentId, lastCallerPosition, ref }: ShareByLinkDialogProps) {
  const { t } = useTranslation();
  const topology = useTopology();
  const psapConfiguration = usePsapConfiguration();
  const defaultCountryCode = (psapConfiguration.psapPortalConfiguration.defaultCountryCode as string | null) ?? '';

  const modalRef = useRef<NgModalHandle>(null);
  const shareRequestRef = useRef(0);

  const [step, setStep] = useState<Step>('pickContact');
  const [targets, setTargets] = useState<readonly ShareLinkTargetDto[]>([]);
  const [loadingTargets, setLoadingTargets] = useState(false);
  const [creating, setCreating] = useState(false);
  const [generatingLink, setGeneratingLink] = useState(false);

  const [searchText, setSearchText] = useState('');
  const [createError, setCreateError] = useState<string | null>(null);

  const [newUserPhoneNumber, setNewUserPhoneNumber] = useState<string | null>(null);
  const [newUserDisplayName, setNewUserDisplayName] = useState<string | null>(null);
  const newUserForm = useEditForm<'phoneNumber' | 'displayName'>({
    fieldValidators: {
      phoneNumber: validateNewUserPhoneNumber,
      displayName: validateNewUserDisplayName,
    },
  });

  const [selectedTarget, setSelectedTarget] = useState<ShareLinkTargetDto | null>(null);
  const [incidentShareUrl, setIncidentShareUrl] = useState<string | null>(null);

  const hasTargetPhoneNumber = (selectedTarget?.phoneNumber ?? '').trim() !== '';

  const resetState = () => {
    shareRequestRef.current += 1;
    setStep('pickContact');
    setTargets([]);
    setSelectedTarget(null);
    setIncidentShareUrl(null);
    setSearchText('');
    setCreateError(null);
    setNewUserDisplayName(null);
    setNewUserPhoneNumber(null);
    newUserForm.reset({ phoneNumber: '', displayName: '' });
  };

  const loadTargets = async () => {
    setLoadingTargets(true);
    try {
      setTargets(await getShareLinkTargets(topology.identityServerUrl));
    } finally {
      setLoadingTargets(false);
    }
  };

  useImperativeHandle(ref, () => ({
    showModal: () => {
      resetState();
      modalRef.current?.show();
      void loadTargets();
    },
  }));

  const closeDialog = () => {
    resetState();
    modalRef.current?.hide();
  };

  const onTargetSelected = async (target: ShareLinkTargetDto) => {
    const requestId = ++shareRequestRef.current;

    setSelectedTarget(target);
    setGeneratingLink(true);
    setStep('shareLink');
    setIncidentShareUrl(null);
    try {
      const url = await createIncidentShareLink(topology.identityServerUrl, incidentId, target.id);
      if (shareRequestRef.current !== requestId) {
        return;
      }
      setIncidentShareUrl(url);
    } finally {
      if (shareRequestRef.current === requestId) {
        setGeneratingLink(false);
      }
    }
  };

  const newContactClicked = () => {
    setCreateError(null);
    const phoneCandidate = extractPhoneCandidate(searchText) ?? defaultCountryCode;
    setNewUserPhoneNumber(phoneCandidate);
    setNewUserDisplayName(null);
    newUserForm.reset({ phoneNumber: phoneCandidate, displayName: '' });
    setStep('createNewContact');
  };

  const createUserAndContinue = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const isValid = newUserForm.validate(() => ({
      phoneNumber: validateNewUserPhoneNumber(newUserPhoneNumber),
      displayName: validateNewUserDisplayName(newUserDisplayName),
    }));
    if (!isValid) {
      return;
    }

    const normalized = normalizePhone(newUserPhoneNumber);

    setCreating(true);
    setCreateError(null);
    try {
      const lookup = await findShareLinkTargetByPhone(topology.identityServerUrl, normalized);

      if (lookup.user !== null) {
        await onTargetSelected(lookup.user);
        return;
      }

      if (lookup.existsButNotShareLinkTarget) {
        setCreateError(t('ShareIncidentDialog_PhoneExistsNotShareable'));
        return;
      }

      const created = await createShareLinkTarget(topology.identityServerUrl, normalized, newUserDisplayName!);
      setTargets((current) => insertTargetSorted(current, created));
      await onTargetSelected(created);
    } catch (error) {
      if (error instanceof CreateShareLinkTargetError && error.status === 400) {
        setCreateError(tryExtractApiErrorMessage(error.responseBody) ?? t('ShareIncidentDialog_CreateUserFailed'));
      } else if (error instanceof CreateShareLinkTargetError) {
        setCreateError(t('ShareIncidentDialog_CreateUserFailed'));
      } else {
        throw error;
      }
    } finally {
      setCreating(false);
    }
  };

  const backToPick = () => {
    shareRequestRef.current += 1;
    setSelectedTarget(null);
    setIncidentShareUrl(null);
    setStep('pickContact');
  };

  const sendSmsLink = async () => {
    if (!hasTargetPhoneNumber || incidentShareUrl == null || incidentShareUrl.trim() === '') {
      return;
    }

    const coordinates = formatCoordinates(lastCallerPosition) ?? '';
    const smsText = t('ShareIncidentDialog_SendIncidentLink_SmsText', { arg0: incidentShareUrl, arg1: coordinates });
    await sendSmsMessage(new PhoneNumber(selectedTarget!.phoneNumber!).value, smsText);
    closeDialog();
  };

  const renderPickStep = () => {
    const filtered = filterShareLinkTargets(targets, searchText);

    return (
      <>
        <p className="text-center">{t('ShareIncidentDialog_PickRecipient_Header')}</p>

        <div className="mb-2">
          <input
            type="search"
            className="form-control"
            placeholder={t('ShareIncidentDialog_Search_Placeholder')}
            value={searchText}
            onChange={(event) => setSearchText(event.target.value)}
          />
        </div>

        {loadingTargets ? (
          <p className="text-center">...</p>
        ) : targets.length === 0 ? (
          <p className="text-muted">{t('ShareIncidentDialog_NoRecipients')}</p>
        ) : filtered.length === 0 ? (
          <p className="text-muted">{t('ShareIncidentDialog_NoSearchMatches')}</p>
        ) : (
          <DataGrid<ShareLinkTargetDto>
            items={filtered}
            selectAction={onTargetSelected}
            height="250px"
            hover
            headerTemplate={
              <>
                <th>{t('ShareIncidentDialog_Column_DisplayName')}</th>
                <th style={{ width: '40px' }}></th>
              </>
            }
            rowTemplate={(target) => (
              <>
                <td>{target.displayName}</td>
                <td>
                  <NgButton
                    icon="fa-regular fa-chevron-right"
                    size="small"
                    type="link"
                    ariaLabel={target.displayName ?? undefined}
                    onClick={(event) => {
                      event.stopPropagation();
                      void onTargetSelected(target);
                    }}
                  />
                </td>
              </>
            )}
          />
        )}
      </>
    );
  };

  const renderNewUserStep = () => (
    <>
      <p className="text-center">{t('ShareIncidentDialog_PickRecipient_Header')}</p>

      <form id={NEW_USER_FORM_ID} onSubmit={(event) => void createUserAndContinue(event)}>
        <div>
          <label htmlFor="new-user-phone" className="label">
            {t('ShareIncidentDialog_PhoneNumber')}
          </label>
          <input
            id="new-user-phone"
            className={newUserForm.fieldCssClass('phoneNumber', 'form-control')}
            value={newUserPhoneNumber ?? ''}
            onChange={(event) => {
              setNewUserPhoneNumber(event.target.value);
              newUserForm.notifyFieldInput('phoneNumber', event.target.value);
            }}
            onBlur={() => newUserForm.notifyFieldBlur('phoneNumber')}
          />
          {newUserForm.validationMessages('phoneNumber').map((messageKey, index) => (
            <div key={index} className="validation-message">
              {t(messageKey)}
            </div>
          ))}
        </div>

        <div className="mt-2">
          <label htmlFor="new-user-name" className="label">
            {t('ShareIncidentDialog_DisplayName')}
          </label>
          <input
            id="new-user-name"
            className={newUserForm.fieldCssClass('displayName', 'form-control')}
            value={newUserDisplayName ?? ''}
            onChange={(event) => {
              setNewUserDisplayName(event.target.value);
              newUserForm.notifyFieldInput('displayName', event.target.value);
            }}
            onBlur={() => newUserForm.notifyFieldBlur('displayName')}
          />
          {newUserForm.validationMessages('displayName').map((messageKey, index) => (
            <div key={index} className="validation-message">
              {t(messageKey)}
            </div>
          ))}
        </div>

        {createError != null && createError !== '' && <div className="text-danger mt-2">{createError}</div>}
      </form>
    </>
  );

  const renderLinkReadyStep = () => (
    <>
      <p className="text-center">{t('ShareIncidentDialog_SharedIncidentAccessTimeNote')}</p>
      <ReadOnlyValue value={incidentShareUrl ?? undefined} isLoading={generatingLink} allowCopyButton />

      <Divider />

      <div className="d-flex flex-column">
        <small className="text-muted">{selectedTarget?.displayName}</small>
        <small className="text-muted">
          {(selectedTarget?.phoneNumber ?? '').trim() === '' ? '-' : selectedTarget!.phoneNumber}
        </small>
      </div>
    </>
  );

  return (
    <NgModal
      ref={modalRef}
      header={t('ShareIncidentDialog_Header')}
      width="medium"
      bodyTemplate={
        step === 'pickContact'
          ? renderPickStep()
          : step === 'createNewContact'
            ? renderNewUserStep()
            : renderLinkReadyStep()
      }
      footerTemplate={
        step === 'pickContact' ? (
          <NgButton text={t('ShareIncidentDialog_NewContact')} onClick={newContactClicked} />
        ) : step === 'createNewContact' ? (
          <>
            <NgButton outline text={t('ShareIncidentDialog_Back')} onClick={backToPick} cssClass="flex-grow-1" />
            <NgButton
              submitButton
              form={NEW_USER_FORM_ID}
              text={t('ShareIncidentDialog_CreateContact')}
              disabled={creating}
              cssClass="flex-grow-1"
            />
          </>
        ) : (
          <>
            <NgButton
              icon="fal fa-paper-plane-top"
              text={t('ShareIncidentDialog_SendSmsLink')}
              disabled={!hasTargetPhoneNumber}
              title={hasTargetPhoneNumber ? undefined : t('ShareIncidentDialog_NoPhoneNumber')}
              onClick={sendSmsLink}
            />
            <NgButton text={t('ShareIncidentDialog_Back')} outline onClick={backToPick} />
          </>
        )
      }
    />
  );
}
